import { api } from './client';
import type {
  AiUsageResponse,
  AuthResponse,
  JobsListResponse,
  MeResponse,
  Plan,
  Subscription,
  PaymentEvent,
  SystemStats,
  UserAuditResponse,
  UserDetailResponse,
  UsersListResponse,
} from './types';

export const auth = {
  login: (email: string, password: string) =>
    api.post<AuthResponse>(
      '/api/v1/auth/login',
      { email, password, deviceId: 'admin-dashboard' },
      { skipAuth: true },
    ),
  me: () => api.get<MeResponse>('/api/v1/auth/me'),
  logout: () => api.post<void>('/api/v1/auth/logout', {}),
};

export const admin = {
  stats: () => api.get<SystemStats>('/api/v1/admin/stats'),

  listUsers: (params: { page?: number; limit?: number; search?: string }) =>
    api.get<UsersListResponse>('/api/v1/admin/users', { query: params }),

  promote: (userId: string) =>
    api.post<{ user: unknown }>(`/api/v1/admin/users/${userId}/promote`),

  demote: (userId: string) =>
    api.post<{ user: unknown }>(`/api/v1/admin/users/${userId}/demote`),

  promoteSuper: (userId: string) =>
    api.post<{ user: unknown }>(`/api/v1/admin/users/${userId}/promote-super`),

  ban: (userId: string) =>
    api.post<{ user: unknown }>(`/api/v1/admin/users/${userId}/ban`),

  unban: (userId: string) =>
    api.post<{ user: unknown }>(`/api/v1/admin/users/${userId}/unban`),

  listJobs: (params: { page?: number; limit?: number; status?: string }) =>
    api.get<JobsListResponse>('/api/v1/admin/jobs', { query: params }),

  broadcast: (params: {
    title: string;
    message: string;
    type?: string;
    targetUserIds?: string[];
  }) => api.post<{ sent: number }>('/api/v1/admin/notifications/broadcast', params),

  aiUsage: (days: number = 30) =>
    api.get<AiUsageResponse>('/api/v1/admin/ai-usage', { query: { days } }),

  userDetail: (userId: string) =>
    api.get<UserDetailResponse>(`/api/v1/admin/users/${userId}`),

  userAudit: (userId: string, before?: string) =>
    api.get<UserAuditResponse>(`/api/v1/admin/users/${userId}/audit`, {
      query: before ? { before } : {},
    }),

  // ── v3 plans CRUD ─────────────────────────────────────────────────────
  listPlans: () => api.get<{ plans: Plan[] }>('/api/v1/admin/plans'),

  createPlan: (plan: Partial<Plan>) =>
    api.post<{ plan: Plan }>('/api/v1/admin/plans', plan),

  updatePlan: (id: string, patch: Partial<Plan>) =>
    api.patch<{ plan: Plan }>(`/api/v1/admin/plans/${id}`, patch),

  deletePlan: (id: string) =>
    api.delete<{ plan: Plan }>(`/api/v1/admin/plans/${id}`),

  // ── v3 subscriptions ──────────────────────────────────────────────────
  listSubscriptions: (params: {
    status?: string;
    userId?: string;
    before?: string;
    limit?: number;
  }) =>
    api.get<{ subscriptions: Subscription[] }>(
      '/api/v1/admin/billing/subscriptions',
      { query: params },
    ),

  cancelSubscription: (id: string) =>
    api.post<{ subscription: Subscription }>(
      `/api/v1/admin/billing/subscriptions/${id}/cancel`,
    ),

  grantSubscription: (params: { userId: string; planId: string; days?: number }) =>
    api.post<{ subscription: Subscription }>(
      '/api/v1/admin/billing/subscriptions/grant',
      params,
    ),

  // ── v3 payment events (Razorpay webhook log) ───────────────────────────
  listPaymentEvents: (params: {
    status?: string;
    eventType?: string;
    userId?: string;
    before?: string;
    limit?: number;
  }) =>
    api.get<{ events: PaymentEvent[] }>('/api/v1/admin/billing/payment-events', {
      query: params,
    }),

  // ── v3 wallet credit (manual grant / adjustment) ──────────────────────
  creditWallet: (params: { userId: string; credits: number; note?: string }) =>
    api.post<void>('/api/v1/admin/billing/wallet/credit', params),

  // ── v3 AI flags (Play AI policy review queue) ─────────────────────────
  listAiFlags: (params: { status?: string; before?: string; limit?: number }) =>
    api.get<{
      flags: Array<{
        id: string;
        userId: string;
        prompt: string;
        output: string;
        reason: string;
        comment: string | null;
        status: string;
        reviewerNotes: string | null;
        createdAt: string;
        reviewedAt: string | null;
        user?: { id: string; email: string; name: string };
      }>;
    }>('/api/v1/admin/ai-flags', { query: params }),

  resolveAiFlag: (id: string, params: { status: string; reviewerNotes?: string }) =>
    api.post<{ flag: unknown }>(`/api/v1/admin/ai-flags/${id}/resolve`, params),

  // ── v3 monthly tax exports (GST / TDS / FIRC) ─────────────────────────
  monthlyExports: (month: string) =>
    api.get<{
      period: { month: string; start: string; end: string };
      summary: {
        inr: { count: number; totalPaise: number; totalRupees: number; gstNote: string };
        export: { count: number; totalCents: number; totalUsd: number; note: string };
      };
      tdsNote: string;
      rows: Array<{
        receivedAt: string;
        eventType: string;
        paymentId: string;
        orderId: string | null;
        userId: string | null;
        amountMinor: number;
        currency: string;
        method: string | null;
        contactEmail: string | null;
        firc: string | null;
        kind: 'domestic_inr' | 'export_service';
      }>;
    }>('/api/v1/admin/exports', { query: { month } }),

  // Sprint 28 — runtime artifact bucket admin (unchanged)
  listRuntimeArtifacts: (params?: { prefix?: string }) =>
    api.get<{
      artifacts: { key: string; sizeBytes: number; lastModified: string }[];
      count: number;
    }>('/api/v1/admin/runtimes/objects', {
      query: params?.prefix ? { prefix: params.prefix } : {},
    }),

  uploadRuntimeArtifact: (form: FormData) =>
    api.post<{
      url: string;
      sha256: string;
      sizeBytes: number;
      key: string;
      knownInCatalog: boolean;
      catalogSnippet: Record<string, unknown>;
    }>('/api/v1/admin/runtimes/upload', form, { multipart: true }),

  deleteRuntimeArtifact: (key: string) =>
    api.delete<{ message: string }>('/api/v1/admin/runtimes/objects', {
      query: { key },
    }),

  listBroadcasts: (params: { before?: string; limit?: number }) =>
    api.get<{
      broadcasts: {
        id: string;
        title: string;
        message: string;
        type: string;
        recipients: number;
        targets: string[] | null;
        sentBy: { id: string; email: string; name: string } | null;
        createdAt: string;
      }[];
      nextCursor: string | null;
      hasMore: boolean;
    }>('/api/v1/admin/broadcasts', {
      query: {
        before: params.before || undefined,
        limit: params.limit ?? 50,
      },
    }),

  listFeedback: (params: {
    category?: string;
    platform?: string;
    search?: string;
    before?: string;
    limit?: number;
  }) =>
    api.get<{
      feedback: {
        id: string;
        userId: string | null;
        email: string | null;
        category: string;
        message: string;
        appVersion: string | null;
        platform: string | null;
        createdAt: string;
      }[];
      nextCursor: string | null;
      hasMore: boolean;
    }>('/api/v1/admin/feedback', {
      query: {
        category: params.category || undefined,
        platform: params.platform || undefined,
        search: params.search || undefined,
        before: params.before || undefined,
        limit: params.limit ?? 50,
      },
    }),
};
